// R-274 Phase 7d — "When to export" authoring panel.
// Lets admins configure an ExportTriggerPolicy on a destination (or inherit
// it from the job-type default). No RPC in this component — caller wires save.

import { useEffect, useState } from 'react';
import type { ExportTriggerPolicy } from '@capture/shared';
import { isStreamingOnlyAdapter } from '@capture/shared';
import {
  panelStateToPolicy,
  validatePanelState,
  describeTriggerPolicy,
  policyToPanelState,
  normalizeForConstraints,
  type PanelState,
} from './trigger-policy-helpers';
import { TIMEZONE_OPTIONS } from './destination-options';
import styles from './TriggerPolicyPanel.module.css';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface TriggerPolicyPanelProps {
  /** The destination's own override, or null when inheriting. */
  policy: ExportTriggerPolicy | null;
  /** The job-type's default policy to display in the inherit chip. */
  inheritedPolicy: ExportTriggerPolicy | null;
  /** Adapter type — 'skarn_product' disables accumulate. */
  adapterType: string;
  /** Job-type processing mode — 'flow' disables batch grouping. */
  processingMode: string;
  /** Called with the new policy, or null to clear the override. */
  onChange: (p: ExportTriggerPolicy | null) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TriggerPolicyPanel({
  policy,
  inheritedPolicy,
  adapterType,
  processingMode,
  onChange,
}: TriggerPolicyPanelProps) {
  const accumulateDisabled = isStreamingOnlyAdapter(adapterType);
  const batchDisabled = processingMode === 'flow';

  // When policy is null, we show the inherit chip.  An "own" panel state is
  // initialized from the current policy (or streaming as default), normalized
  // so a stale batch grouping never survives into a flow-mode panel.
  const [state, setState] = useState<PanelState>(() =>
    normalizeForConstraints(policyToPanelState(policy), { batchDisabled }),
  );

  // Sync from props when policy changes externally (e.g. parent resets it) or
  // when the batch-disabled constraint changes (job type switched to flow).
  useEffect(() => {
    setState(normalizeForConstraints(policyToPanelState(policy), { batchDisabled }));
  }, [policy, batchDisabled]);

  // Keep the panel visible even when own policy is null — if the user clicks
  // "Set override" we pre-populate the panel from inherited and let them edit.
  const showOwnEditor = policy !== null;

  const validationError = validatePanelState(state);

  // Single commit path: normalize for constraints, set state, and emit only a
  // valid policy. Every state transition routes through here so batch grouping
  // is never emitted while batchDisabled is true.
  function applyState(next: PanelState) {
    const normalized = normalizeForConstraints(next, { batchDisabled });
    setState(normalized);
    if (validatePanelState(normalized) === null) {
      onChange(panelStateToPolicy(normalized));
    }
  }

  function update(patch: Partial<PanelState>) {
    applyState({ ...state, ...patch });
  }

  // ── mode radio ─────────────────────────────────────────────────────────────
  function handleModeChange(mode: 'streaming' | 'accumulate') {
    applyState({
      ...state,
      mode,
      // Default to rolling when switching to accumulate (batch can be set next).
      grouping: mode === 'accumulate' ? (state.grouping ?? 'rolling') : undefined,
    });
  }

  function handleGroupingChange(grouping: 'rolling' | 'batch') {
    applyState({ ...state, grouping });
  }

  // Derive echo text from a valid state (ignore invalid mid-edit states)
  const echoPolicy =
    validationError === null ? panelStateToPolicy(state) : null;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className={styles.panel}>

      {/* ── Inherit chip — shown only when no own override + inherited exists ── */}
      {!showOwnEditor && inheritedPolicy !== null && (
        <div className={styles.inheritChip} data-testid="inherit-chip">
          <span className={styles.inheritLabel}>Inheriting</span>
          <div className={styles.inheritBody}>
            <span className={styles.inheritDesc}>
              {describeTriggerPolicy(inheritedPolicy)}
            </span>
            <div className={styles.inheritActions}>
              <button
                type="button"
                className={styles.inheritSetBtn}
                onClick={() => {
                  // Initialize editor from the inherited policy (normalized so
                  // an inherited batch grouping isn't pinned on a flow-mode job).
                  applyState(policyToPanelState(inheritedPolicy));
                }}
              >
                Set override
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mode ─────────────────────────────────────────────────────────── */}
      <div className={styles.section}>
        <p className={styles.sectionLabel}>Export trigger mode</p>
        <div className={styles.radioGroup}>
          <label className={styles.radioRow}>
            <input
              type="radio"
              name="trigger-mode"
              value="streaming"
              checked={state.mode === 'streaming'}
              onChange={() => handleModeChange('streaming')}
            />
            <span className={styles.radioLabel}>
              <span className={styles.radioTitle}>Streaming</span>
              <span className={styles.radioHelp}>
                Export each document as soon as it completes the pipeline.
              </span>
            </span>
          </label>

          <label className={styles.radioRow}>
            <input
              type="radio"
              name="trigger-mode"
              value="accumulate"
              checked={state.mode === 'accumulate'}
              disabled={accumulateDisabled}
              onChange={() => handleModeChange('accumulate')}
              aria-label="Accumulate"
            />
            <span className={styles.radioLabel}>
              <span
                className={
                  accumulateDisabled ? styles.radioTitleDisabled : styles.radioTitle
                }
              >
                Accumulate
              </span>
              <span className={styles.radioHelp}>
                Pool documents and export as a group.
                {accumulateDisabled && (
                  <> (Not available for the <em>skarn_product</em> adapter.)</>
                )}
              </span>
            </span>
          </label>
        </div>
      </div>

      {/* ── Grouping (accumulate only) ────────────────────────────────────── */}
      {state.mode === 'accumulate' && (
        <div className={styles.section}>
          <p className={styles.sectionLabel}>Grouping</p>
          <div className={styles.radioGroup}>
            <label className={styles.radioRow}>
              <input
                type="radio"
                name="trigger-grouping"
                value="rolling"
                checked={state.grouping === 'rolling'}
                onChange={() => handleGroupingChange('rolling')}
                aria-label="Rolling"
              />
              <span className={styles.radioLabel}>
                <span className={styles.radioTitle}>Rolling</span>
                <span className={styles.radioHelp}>
                  Flush when count, age, or schedule threshold is met.
                </span>
              </span>
            </label>

            <label className={styles.radioRow}>
              <input
                type="radio"
                name="trigger-grouping"
                value="batch"
                checked={state.grouping === 'batch'}
                disabled={batchDisabled}
                onChange={() => handleGroupingChange('batch')}
                aria-label="Batch"
              />
              <span className={styles.radioLabel}>
                <span
                  className={batchDisabled ? styles.radioTitleDisabled : styles.radioTitle}
                >
                  Batch
                </span>
                <span className={styles.radioHelp}>
                  Export once per ingest batch when all documents are ready.
                  {batchDisabled && (
                    <> (Not available for <em>flow</em> processing mode — use Rolling.)</>
                  )}
                </span>
              </span>
            </label>
          </div>
        </div>
      )}

      {/* ── Rolling flush conditions ──────────────────────────────────────── */}
      {state.mode === 'accumulate' && state.grouping === 'rolling' && (
        <div className={styles.section}>
          <p className={styles.sectionLabel}>Flush conditions (any one triggers export)</p>

          {/* Count */}
          <div className={styles.conditionRow}>
            <label className={styles.conditionCheckbox}>
              <input
                type="checkbox"
                checked={!!state.maxCountOn}
                onChange={(e) => update({ maxCountOn: e.target.checked })}
                aria-label="Max count"
              />
              <span className={styles.conditionCheckLabel}>Max count</span>
            </label>
            <input
              type="number"
              className={styles.conditionInput}
              aria-label="Max count"
              value={state.maxCount ?? ''}
              min={1}
              step={1}
              disabled={!state.maxCountOn}
              onChange={(e) =>
                update({ maxCount: e.target.value ? parseInt(e.target.value, 10) : undefined })
              }
            />
            <span className={styles.conditionUnit}>docs</span>
          </div>

          {/* Age */}
          <div className={styles.conditionRow}>
            <label className={styles.conditionCheckbox}>
              <input
                type="checkbox"
                checked={!!state.maxAgeOn}
                onChange={(e) => update({ maxAgeOn: e.target.checked })}
                aria-label="Max age"
              />
              <span className={styles.conditionCheckLabel}>Max age</span>
            </label>
            <input
              type="number"
              className={styles.conditionInput}
              aria-label="Max age"
              value={state.maxAgeMinutes ?? ''}
              min={1}
              step={1}
              disabled={!state.maxAgeOn}
              onChange={(e) =>
                update({
                  maxAgeMinutes: e.target.value ? parseInt(e.target.value, 10) : undefined,
                })
              }
            />
            <span className={styles.conditionUnit}>minutes</span>
          </div>

          {/* Cron */}
          <div className={styles.conditionRow}>
            <label className={styles.conditionCheckbox}>
              <input
                type="checkbox"
                checked={!!state.cronOn}
                onChange={(e) => update({ cronOn: e.target.checked })}
                aria-label="Schedule (cron)"
              />
              <span className={styles.conditionCheckLabel}>Schedule</span>
            </label>
            <input
              type="text"
              className={`${styles.conditionInput} ${styles.conditionInputWide}`}
              aria-label="Schedule (cron)"
              value={state.cron ?? ''}
              placeholder="0 23 * * *"
              disabled={!state.cronOn}
              spellCheck={false}
              onChange={(e) => update({ cron: e.target.value })}
            />
          </div>

          {/* Timezone (only when cron is on) */}
          {state.cronOn && (
            <div className={styles.conditionRow}>
              <span className={styles.conditionCheckbox} /> {/* indent */}
              <select
                className={styles.conditionTimezone}
                value={state.timezone ?? 'UTC'}
                disabled={!state.cronOn}
                onChange={(e) => update({ timezone: e.target.value })}
                aria-label="Timezone for cron"
              >
                {TIMEZONE_OPTIONS.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Validation error */}
          {validationError && (
            <p className={styles.validationError} role="alert">
              {validationError}
            </p>
          )}
        </div>
      )}

      {/* ── Plain-language echo ───────────────────────────────────────────── */}
      {echoPolicy !== null && (
        <p className={styles.echo} data-testid="policy-echo">
          {describeTriggerPolicy(echoPolicy)}
        </p>
      )}

      {/* ── Clear override affordance (when own policy set + inherited exists) */}
      {showOwnEditor && inheritedPolicy !== null && (
        <button
          type="button"
          className={styles.clearOverrideBtn}
          onClick={() => onChange(null)}
          aria-label="Clear override"
        >
          Clear override
        </button>
      )}
    </div>
  );
}
